import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Lock, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react'
import authService from '../../services/authService'
import Logo from '../../components/Logo'

const schema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ResetPasswordForm = z.infer<typeof schema>

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>()
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form')
  const [errorMessage, setErrorMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return
    setIsLoading(true)
    try {
      await authService.resetPassword(token, data.password)
      setStatus('success')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      setErrorMessage(err.response?.data?.message || 'Reset link is invalid or expired.')
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8 bg-secondary-50">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo to="/" height={40} />
        </div>

        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-card border border-secondary-200 p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-secondary-900 mb-2">Password Reset!</h1>
            <p className="text-secondary-500 mb-6">
              Your password has been updated successfully. You can now sign in with your new password.
            </p>
            <Link to="/login" className="btn btn-primary w-full">
              Sign In
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-card border border-secondary-200 p-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-secondary-900 mb-2">Reset Failed</h1>
            <p className="text-secondary-500 mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <Link to="/forgot-password" className="btn btn-primary w-full">
                Request New Link
              </Link>
              <Link to="/login" className="btn btn-secondary w-full">
                Back to Sign In
              </Link>
            </div>
          </div>
        )}

        {status === 'form' && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-secondary-900">Set New Password</h1>
              <p className="text-secondary-500 mt-2">
                Enter your new password below
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-card border border-secondary-200 p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label htmlFor="password" className="label">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`input pl-12 pr-12 ${errors.password ? 'border-red-500' : ''}`}
                      placeholder="At least 8 characters"
                      {...register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1.5">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
                    <input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      className={`input pl-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                      placeholder="Repeat your password"
                      {...register('confirmPassword')}
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1.5">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-accent w-full py-3 text-base font-semibold flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
